import { Fragment, h } from "preact";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "preact/hooks";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import i18n from "../i18n.js";
import { createToastMessage, setLoading } from "../store/uiSlice.js";
import { setNotifications } from "../store/notificationsSlice.js";
import projectsApi from "../api/projects.js";
import { getRouteQueryParams, renderHtml } from "../helpers.js";
import projectUnsubscriptionApi from "../api/project_notification_unsubscriptions.js";

import AppFooter from "../components/AppFooter.jsx";
import AppHeader from "../components/AppHeader.jsx";
import AppLayout from "../components/AppLayout.jsx";
import Button from "../components/Button.jsx";
import EmptyState from "../components/EmptyState.jsx";
import ListItem from "../components/ListItem.jsx";
import ProjectItem from "../components/projects/ProjectItem.jsx";
import TalentTable from "../components/projects/TalentTable.jsx";
import ProjectAccessDenied from "./ProjectAccessDenied.jsx";
import Breadcrumbs from "../components/Breadcrumbs.jsx";

export default function ProjectsPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const dispatch = useDispatch();
  const [accessDenied, setAccessDenied] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(async () => {
    const params = getRouteQueryParams(window.location.href);

    if (params.unsubscribe) {
      await projectUnsubscriptionApi.unsubscribe(id);
      createToastMessage(dispatch, {
        type: "success",
        message: i18n.t(`toasts.success.project_unsubscribed`),
      });
    }
  }, []);

  useEffect(async () => {
    dispatch(setLoading(true));
    try {
      const response = await projectsApi.fetch(id);

      setProject(response);
      dispatch(setNotifications(response.notifications));
    } catch (err) {
      if (err.status === 403) {
        setAccessDenied(true);
      } else {
        createToastMessage(dispatch, {
          type: "info",
          message: i18n.t("pages.projects.errors.not_found"),
        });
        navigate("/projects");
      }
    }
    dispatch(setLoading(false));
    setLoaded(true);
  }, []);

  function handleTalentStateUpdated(talentState) {
    const updatedProject = {
      ...project,
      talents: project.talents.map((talent) =>
        talent.talent.id === talentState.talentId
          ? {
              ...talent,
              talent: {
                ...talent.talent,
                state: talentState.state,
              },
            }
          : talent
      ),
    };

    setProject(updatedProject);
  }

  async function handleDeletionFromProject(talent) {
    try {
      await projectsApi.removeTalent(project.id, talent.id);
      setProject({
        ...project,
        talents: project.talents.filter((t) => t.talent.id !== talent.id),
      });
      createToastMessage(dispatch, {
        type: "info",
        message: i18n.t("pages.projects.toasts.talent_removed"),
      });
    } catch (err) {
      console.error(err);
    }
  }

  if (accessDenied) {
    return <ProjectAccessDenied />;
  }

  return (
    <AppLayout
      header={
        <AppHeader
          title={i18n.t("pages.titles.projects")}
          filterBar={
            <>
              {project && (
                <Button
                  small
                  text
                  iconFront="edit"
                  to={`/projects/${project.id}/edit?backTo=${location.pathname}`}
                >
                  {i18n.t("pages.projects.editButton")}
                </Button>
              )}
              <Button
                small
                iconFront="add"
                to={`/projects/add?backTo=${location.pathname}`}
              >
                {i18n.t("pages.projects.createButton")}
              </Button>
            </>
          }
        />
      }
      footer={<AppFooter />}
    >
      {loaded && project && (
        <div class="mt-48">
          <Breadcrumbs
            class="mb-24"
            items={[
              {
                to: "/projects",
                title: i18n.t("pages.titles.projects"),
              },
              i18n.t("breadcrumbs.project"),
            ]}
          />
          {project && <ProjectItem onShowPage project={project} />}

          {project.talents && project.talents.length > 0 ? (
            <div class="mt-48">
              <TalentTable
                project={project}
                talents={project.talents}
                onUpdateTalentState={handleTalentStateUpdated}
                onDeleteFromProject={handleDeletionFromProject}
              />

              <ListItem
                class="mt-64"
                iconFront={
                  <img src="/images/icon_calendar.svg" width="48" height="48" />
                }
              >
                <>
                  <h5 class="t-subtitle2 t-subtitle2--margin-small">
                    {i18n.t("pages.projects.more.title")}
                  </h5>
                  <p class="t-body4">
                    {renderHtml(
                      i18n.t("pages.projects.more.description", {
                        link: "/talents",
                      })
                    )}
                  </p>
                </>
              </ListItem>
            </div>
          ) : (
            <EmptyState
              icon="projects_add"
              title={i18n.t("pages.projects.searchingState.title")}
              description={i18n.t("pages.projects.searchingState.description")}
            >
              <Button to="/talents">
                {i18n.t("pages.projects.searchingState.action")}
              </Button>
            </EmptyState>
          )}
        </div>
      )}
    </AppLayout>
  );
}
